import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/marketing/components/ui/button";
import { PRODUCT_QUERIES } from "@/marketing/infrastructure/product/utils/queries";
import type { CampaignInfluencer } from "@/marketing/models/campaignInfluencer";
import type { InfluencerCampaign } from "@/marketing/models/influencerCampaign";

import { ProductChoicePicker } from "../creators/ProductChoicePicker";
import { buildCampaignProductSelectionState } from "../creators/campaignProductSelection";

interface ProductAssignmentSectionProps {
  campaign: InfluencerCampaign;
  influencer: CampaignInfluencer;
}

// Choice-reward campaigns only. Shows the assigned products (thumbnail + shade), or an Assign button when
// the creator has no selection — never both, so an existing pick can't be overwritten from here.
export function ProductAssignmentSection({
  campaign,
  influencer,
}: ProductAssignmentSectionProps) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const productSelectionState = buildCampaignProductSelectionState(campaign, []);
  if (
    !productSelectionState.isRequired ||
    productSelectionState.poolSkus.length === 0
  ) {
    return null;
  }

  const selectedSkus = influencer.selectedSkus ?? [];

  const handleOpenPicker = () => setPickerOpen(true);
  const handlePickerOpenChange = (next: boolean) => {
    if (!next) setPickerOpen(false);
  };

  return (
    <section>
      <h3 className="tw-mb-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
        Products
      </h3>
      {selectedSkus.length > 0 ? (
        <AssignedProducts skus={selectedSkus} />
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="tw-h-8 tw-text-[12px]"
          onClick={handleOpenPicker}
        >
          Assign
        </Button>
      )}
      {pickerOpen && (
        <ProductChoicePicker
          campaignId={campaign.campaignId}
          campaign={campaign}
          creator={influencer}
          open
          onOpenChange={handlePickerOpenChange}
        />
      )}
    </section>
  );
}

function AssignedProducts({ skus }: { skus: string[] }) {
  const productsQuery = useQuery(PRODUCT_QUERIES.bySkus(skus));
  const productBySku = new Map(
    (productsQuery.data ?? []).map((product) => [product.sku, product]),
  );

  return (
    <ul className="tw-flex tw-flex-col tw-gap-2">
      {skus.map((sku) => {
        const product = productBySku.get(sku);
        return (
          <li key={sku} className="tw-flex tw-items-center tw-gap-2.5">
            {product?.productImageUrls[0] ? (
              <img
                src={product.productImageUrls[0]}
                alt=""
                className="tw-h-9 tw-w-9 tw-flex-shrink-0 tw-rounded tw-object-cover"
              />
            ) : (
              <div className="tw-h-9 tw-w-9 tw-flex-shrink-0 tw-rounded tw-bg-muted" />
            )}
            <div className="tw-min-w-0">
              <div className="tw-truncate tw-text-[12px] tw-font-medium tw-text-foreground">
                {product?.color ?? product?.name ?? sku}
              </div>
              <div className="tw-truncate tw-text-[11px] tw-text-muted-foreground">
                {sku}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
